import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Dropzone from "react-dropzone";
import {
  Text,
  Flex,
  Button,
  FormControl,
  FormLabel,
  Input,
  Tooltip,
  Icon,
  IconButton,
  useColorModeValue,
} from "@chakra-ui/react";
import { FaTrash, FaDownload } from "react-icons/fa";
import { useCustomToast } from "components/CustomToast/CustomToast";
import ConfirmDialog from "components/Dialog/ConfirmDialog";

export const FileAttachmentInput = React.forwardRef((props, ref) => {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const showToast = useCustomToast();
  const [touched, setTouched] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const imageBg = useColorModeValue("white", "navy.900");
  const imageBorder = useColorModeValue("gray.200", "gray.400");

  const {
    isDisabled = false,
    isRequired = false,
    translationKey = "documents.form",
    label,
    nameVariable,                  
    acceptedTypes = ["*"],
    initialFile = null,     
    onFileChange,
    downloadUrl,
    onDownload,
    onConfirmDelete,
    downloadFilename,
    ...rest
  } = props;

  const dialogRef = useRef(undefined);

  useEffect(() => {
    if (!initialFile) return;

    if (initialFile instanceof File) {
      const next = { name: initialFile.name, file: initialFile };
      setFile(next);
      onFileChange?.(next);
      return;
    }

    if (typeof initialFile === "object" && initialFile.file instanceof File) {
      const next = {
        name: initialFile.name ?? initialFile.file.name ?? "file",
        file: initialFile.file,
      };
      setFile(next);
      onFileChange?.(next);
    }
  }, [initialFile, onFileChange]);

  const clearFile = useCallback(() => {
    setFile(null);
    onFileChange?.(null);
  }, [onFileChange]);

  const handleClear = useCallback(
    (e) => {
      e.stopPropagation();

      const currentFile = file?.file;

      const initialAsFile =
      initialFile instanceof File
        ? initialFile
        : (initialFile && initialFile.file instanceof File ? initialFile.file : null);

      const isSameAsInitial = !!initialAsFile && currentFile === initialAsFile;
  

      if (onConfirmDelete && (isSameAsInitial || (!currentFile && initialAsFile))) {
        dialogRef.current?.displayDialog({
          title: t("documents.list.actions.remove"),
          message: t("system.actions.remove-confirm"),
          onConfirm: async () => {
            await onConfirmDelete();
            clearFile();
          },
        });
      } else {
        clearFile();
      }
    },
    [file, initialFile, onConfirmDelete, t, clearFile]
  );

  const onError = useCallback(() => {
    showToast({
      title: t("system.messages.errors.invalid-file", { defaultValue: "Arquivo inválido" }),
      status: "error",
    });
  }, [showToast, t]);

  const onDropAccepted = useCallback(
    (acceptedFiles) => {
      setTouched(true);
      if (acceptedFiles.length > 0) {
        const f = acceptedFiles[0];
        const next = { name: f.name, file: f };
        setFile(next);
        onFileChange?.(next);
      }
    },
    [onFileChange]
  );

  const triggerDownload = useCallback((blobOrFile, nameFallback) => {
    const filename =
      (blobOrFile && "name" in blobOrFile && blobOrFile.name) ||
      file?.name ||
      downloadFilename ||
      nameFallback ||
      nameVariable ||
      "download";
    const url = URL.createObjectURL(blobOrFile);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [file?.name, downloadFilename, nameVariable]);

  const handleDownload = useCallback(async (e) => {
    e.stopPropagation();
    try {
      setDownloading(true);

      if (downloadUrl) {
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = file?.name || downloadFilename || nameVariable || "download";
        document.body.appendChild(a);
        a.click();
        a.remove();
        return;
      }

      if (file?.file instanceof File) {
        triggerDownload(file.file);
        return;
      }

      if (onDownload) {
        const res = await onDownload();

        if (res instanceof File) {
          triggerDownload(res);
          return;
        }
        if (res instanceof Blob) {
          triggerDownload(res);
          return;
        }
        if (res?.file instanceof File) {
          triggerDownload(res.file, res.name);
          return;
        }
        if (res?.blob instanceof Blob) {
          const f = new File([res.blob], res.filename || (downloadFilename || nameVariable || "download"), { type: res.blob.type || "application/octet-stream" });
          triggerDownload(f);
          return;
        }

        throw new Error("INVALID_DOWNLOAD_RESPONSE");
      }

      showToast({
        title: t("system.messages.errors.download-not-available", { defaultValue: "Download indisponível" }),
        status: "warning",
      });
    } catch (err) {
      showToast({
        title: t("system.messages.errors.general", { defaultValue: "Falha ao baixar arquivo" }),
        status: "error",
      });
    } finally {
      setDownloading(false);
    }
  }, [downloadUrl, file?.file, downloadFilename, nameVariable, onDownload, showToast, t, triggerDownload]);

  const canDownload = useMemo(() => {
    if (downloadUrl) return true;
    if (file?.file instanceof File) return true;
    if (nameVariable && onDownload) return true;
    return false;
  }, [downloadUrl, file?.file, nameVariable, onDownload]);

  useImperativeHandle(ref, () => ({
    getFile: () => file,
    isValid: () => !isRequired || !!file,
    markTouched: () => setTouched(true),
    setFile: (next) => {
      if (next instanceof File) {
        const shaped = { name: next.name, file: next };
        setFile(shaped);
        onFileChange?.(shaped);
      } else if (next && next.file instanceof File) {
        const shaped = {
          name: next.name ?? next.file.name ?? "file",
          file: next.file,
        };
        setFile(shaped);
        onFileChange?.(shaped);
      } else if (!next) {
        clearFile();
      }
    },
    downloadCurrent: async () => handleDownload(new MouseEvent("click")),
  }));

  useEffect(() => {
    if (!initialFile) {
      setFile(null);
      onFileChange?.(null);
      setTouched(false);
      return;
    }

    const asFile =
      initialFile instanceof File
        ? initialFile
        : (typeof initialFile === "object" && initialFile.file instanceof File ? initialFile.file : null);

    if (!asFile) {
      setFile(null);
      onFileChange?.(null);
      setTouched(false);
      return;
    }

    const next = {
      name: (typeof initialFile === "object" && initialFile.name) || asFile.name || "file",
      file: asFile,
    };
    setFile(next);
    onFileChange?.(next);
    setTouched(false);
  }, [initialFile, onFileChange]); 

  return (
    <>
      <FormControl
        isRequired={isRequired}
        isInvalid={isRequired && touched && !file}
        {...rest}
      >
        <FormLabel fontSize="xs" fontWeight="bold">
          {label ?? t(`${translationKey}.fields.attachments.label`)}
        </FormLabel>
        <Dropzone
          disabled={isDisabled}
          onDropAccepted={onDropAccepted}
          onDropRejected={(e) => {
            setTouched(true);
            onError(e);
          }}
          accept={acceptedTypes}
        >
          {({ getRootProps, getInputProps }) => (
            <Flex
              align="center"
              justify="center"
              gap="8px"
              cursor="pointer"
              border="2px dashed"
              borderRadius="8px"
              bg={imageBg}
              borderColor={imageBorder}
              px="8px"
              py="6px"
              {...getRootProps({ className: "dropzone" })}
            >
              <Input
                {...getInputProps({
                  onClick: () => setTouched(true),
                })}
                accept={acceptedTypes.join(",")}
              />

              <Button fontSize="sm" variant="no-effects">
                <Text
                  fontWeight="normal"
                  color={file || nameVariable ? "inherit" : "gray.400"}
                >
                  {file?.name || nameVariable || t("system.placeholders.file-dropzone")}
                </Text>
              </Button>

              {/* ações */}
              {(file || canDownload || onConfirmDelete) && (
                <Flex align="center" gap="6px">
                  {canDownload && (
                    <>
                    <Tooltip hasArrow label={t("system.actions.download", { defaultValue: "Download" })}>
                      <IconButton
                        size="xs"
                        aria-label={t("system.actions.download", { defaultValue: "Download" })}
                        onClick={handleDownload}
                        isDisabled={isDisabled}
                        isLoading={downloading}
                        icon={<Icon as={FaDownload} />}
                      />
                    </Tooltip>
                    <Tooltip hasArrow label={t("system.actions.clear")}>
                      <IconButton
                        size="xs"
                        colorScheme="red"
                        aria-label={t("system.actions.clear")}
                        onClick={handleClear}
                        icon={<Icon as={FaTrash} />}
                        isDisabled={isDisabled}
                      />
                    </Tooltip>
                    </>
                  )}

                </Flex>
              )}
            </Flex>
          )}
        </Dropzone>

        {isRequired && touched && !file && (
          <Text color="red.500" fontSize="xs" mt="1">
            {t("system.messages.errors.required-field")}
          </Text>
        )}
      </FormControl>
      {onConfirmDelete && 
      <ConfirmDialog ref={dialogRef} onConfirm={onConfirmDelete} confirmButtonProps={{ colorScheme: "red" }} />  
      }
    </>
  );
});
